import React from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { I18n } from '../../../../Locales/I18nService';
import { SingleChoiceDialog } from '../../../Pages/Projects/Components/ChoiceDialog/SingleChoiceDialog';
import { useCurrentProject } from '../../../../Configs/CurrentProjectContext';
import { useQuoteSaveController } from '../../../Pages/Quote/Controllers/QuoteSaveController';
import { useQuery } from '../../../Hooks/URL/URLUtils';
import { RouteQueryParams } from '../../../../Types/RouteParams';
import { NavigationService } from '../../../Services/NavigationService';
import { LogLevelType } from '../../../../Services/Analytics/LogLevelType';
import { Logger } from '../../../../Errors/Logger';
import { EstimationUrlParameters, UrlHelper } from 'Helpers';
import { MapError } from './MapErrorBoundary';

const FallbackRender = (props: FallbackProps) => {
    //! todo : CurrentProjectContext.Provider not accessible. currentProject.getState()?.project is always undefined
    const currentProject = useCurrentProject();
    const project = currentProject.getState()?.projet;
    const projectInProgress = Boolean(project);
    return projectInProgress ? (
        <FallBackDialogProjectInProgress {...props} />
    ) : (
        <FallBackDialogNoProjectInProgress {...props} />
    );
};

const FallBackDialogProjectInProgress = (props: FallbackProps) => {
    const version = useQuery<RouteQueryParams>().version;
    const { requestCreateProject } = useQuoteSaveController();
    return (
        <SingleChoiceDialog
            title={I18n.get('GlobalErrorFallbackDialog_Title')}
            body={I18n.get('GlobalErrorFallbackDialog_Subtitle_ProjectInProgress')}
            labelChoice={I18n.get('COMMON_CANCEL')}
            onClickChoice={() => {
                requestCreateProject({
                    forceSave: false,
                    callback: () => {
                        NavigationService.goToMyProjects(UrlHelper.toQueryParams<EstimationUrlParameters>({ version }));
                    },
                    onCancel: () => {
                        NavigationService.goToMyProjects(UrlHelper.toQueryParams<EstimationUrlParameters>({ version }));
                    },
                });
            }}
            onClose={() => {
                NavigationService.goToMyProjects(UrlHelper.toQueryParams<EstimationUrlParameters>({ version }));
            }}
            styles={{ content: { paddingBottom: 24 } }}>
            <MapError {...props} />
        </SingleChoiceDialog>
    );
};

const FallBackDialogNoProjectInProgress = (props: FallbackProps) => {
    return (
        <SingleChoiceDialog
            title={I18n.get('GlobalErrorFallbackDialog_Title')}
            body={I18n.get('GlobalErrorFallbackDialog_Subtitle_NoProjectInProgress')}
            labelChoice={I18n.get('COMMON_REFRESH')}
            onClickChoice={() => {
                window.location.reload();
            }}
            onClose={() => {
                window.location.reload();
            }}
            styles={{ content: { paddingBottom: 24 } }}>
            <MapError {...props} />
        </SingleChoiceDialog>
    );
};

const LOG_MSG = 'MAP_ERROR_BOUNDARY_PREVENT_WSOD';

export const GlobalErrorBoundary = ({ children }: React.PropsWithChildren<{}>) => {
    return (
        <ErrorBoundary
            fallbackRender={(props) => {
                return <FallbackRender {...props} />;
            }}
            onError={(error) => {
                Logger.logAnalytics({
                    message: LOG_MSG,
                    level: LogLevelType.Error,
                    value: error,
                });
            }}>
            {children}
        </ErrorBoundary>
    );
};
